import { differenceInDays } from 'date-fns';

import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { useIsAdmin } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useConnectorOnboardingState } from './useConnectorOnboardingState.ts';

const FIRST_THIRTY_DAYS_THRESHOLD = 30;

/**
 * Hook for consumers to check if the connector onboarding experiment is enabled.
 * Use this to conditionally render a ConnectorPromo* entry point component.
 *
 * Eligibility criteria:
 * - Rovo must be enabled
 * - User must have admin access
 * - User must be a founding admin (exclude nth users)
 * - User must be within the first 30 days of their enrolment date
 *
 * @param numberOfDays - Optional number of days since enrolment to consider the user eligible.
 *   Defaults to `FIRST_THIRTY_DAYS_THRESHOLD` (30 days).
 *
 * @example
 * ```tsx
 * // Default (30 days)
 * const { isEnabled } = useIsConnectorOnboardingEnabled();
 *
 * // Custom threshold
 * const { isEnabled } = useIsConnectorOnboardingEnabled(60);
 *
 * {isEnabled && <ConnectorPromoNavSearch />}
 * ```
 */
export function useIsConnectorOnboardingEnabled(
	numberOfDays: number = FIRST_THIRTY_DAYS_THRESHOLD,
): { isEnabled: boolean } {
	const { isRovoEnabled } = useSessionData();
	const isAdmin = useIsAdmin();

	const shouldSkipForPreConditions = !isRovoEnabled || !isAdmin;

	const {
		onboardingState,
		loading: isOnboardingStateLoading,
		error: onboardingStateError,
	} = useConnectorOnboardingState({
		skip: !fg('twcg-1489-cc-3p-connector-onboarding-fg') || shouldSkipForPreConditions,
	});

	if (shouldSkipForPreConditions || isOnboardingStateLoading) {
		return { isEnabled: false };
	}

	// Unauthorized errors are already marked as handled inside useConnectorOnboardingState.
	// Non-unauthorized errors are left unhandled so they reach error monitoring.
	if (onboardingStateError) {
		return { isEnabled: false };
	}

	const { enrolmentDate, promoDismissed } = onboardingState ?? {};

	// Skip when the user has dismissed both promo surfaces
	if (promoDismissed === 'both') {
		return { isEnabled: false };
	}

	// Only show to users within the first 30 days of their experiment enrolment
	const isWithinFirstThirtyDays =
		Boolean(enrolmentDate) &&
		differenceInDays(new Date(), new Date(enrolmentDate as string)) < numberOfDays;

	if (!isWithinFirstThirtyDays) {
		return { isEnabled: false };
	}

	return { isEnabled: expVal('twcg-1489-cc-3p-connector-onboarding', 'isEnabled', false) };
}
